import { CabButton, CabIcon, CabModal } from "@CabComponents";
import {
  Box, ButtonBase, FormControlLabel, Menu, MenuItem, Radio, RadioGroup,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import { IoArrowBack, IoCheckmark, IoChevronDown, IoLogOutOutline } from 'react-icons/io5';
import { ReactElement, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import colors from "../../../colors";
import { NAVY_LOGO_SRC } from "../../../resourceUrls";
import { EVENT_TYPE, NEW_TIER, TIER } from "../../../constants";
import { trackEventWithExtra } from "../../../utils/appAnalyticsUtils";

export interface FormInput {
  billedAnnually: boolean;
  planTier: TIER;
}

export interface PlanSelectionProps {
  account?: { name: string; email: string };
  onBack?: () => void;
  defaultSelection?: {
    billedAnnually?: boolean;
    planTier?: TIER;
  };
  planOptions: {
    id: TIER;
    name: string;
    costMonthly: number;
    costAnnually: number;
  }[];
  comparisonRows: {
    label: string;
    description?: string;
    values: Partial<Record<TIER, string | boolean>>;
  }[];
  dialogError?: string;
  onDismissError: () => void;
  onFinish: (values: FormInput) => void;
  onLogout: () => void;
}

const formatPrice = (value: number) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 2, minimumFractionDigits: 0,
}).format(value);

const PlanSelection = ({
  account, onBack, defaultSelection, planOptions, comparisonRows, dialogError, onDismissError, onFinish, onLogout
}: PlanSelectionProps): ReactElement => {
  const { control, formState: { isSubmitting }, handleSubmit: handleFormSubmit, watch, setValue } = useForm<FormInput>({
    defaultValues: { billedAnnually: false, planTier: NEW_TIER.CORE },
  });

  useEffect(() => {
    if (defaultSelection?.planTier) {
      setValue('planTier', defaultSelection.planTier);
    }
    if (defaultSelection?.billedAnnually !== undefined) {
      setValue('billedAnnually', defaultSelection.billedAnnually);
    }
  }, [defaultSelection?.billedAnnually, defaultSelection?.planTier, setValue]);

  const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement | null>(null);
  const accountMenuOpen = Boolean(accountMenuAnchor);
  const billedAnnually = watch('billedAnnually');
  const firstPlanSavings = planOptions.length
    ? (planOptions[0].costMonthly - planOptions[0].costAnnually) * 12 : 0;
  const sharedAnnualSavings = planOptions.every(plan =>
    (plan.costMonthly - plan.costAnnually) * 12 === firstPlanSavings) ? firstPlanSavings : 0;
  const choosePlan = (planTier: TIER) => {
    setValue('planTier', planTier, { shouldDirty: true, shouldTouch: true });
    trackEventWithExtra({ eventName: EVENT_TYPE.ONBOARD_TIER_SELECTION, extra: { tier: planTier } });
    return handleFormSubmit(onFinish)();
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100%', display: 'flex', flexDirection: 'column',
      backgroundColor: colors.systemNeutral }}>
      <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: theme => theme.zIndex.appBar,
        borderBottom: 1, borderColor: colors.calmGrey200, backgroundColor: colors.systemWhite }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
          width: '100%', maxWidth: 1120, marginX: 'auto', paddingX: { xs: 2, sm: 3, md: 4 },
          paddingY: { xs: 1, sm: 1.5 }, boxSizing: 'border-box' }}>
          <Box component="img" src={NAVY_LOGO_SRC} alt="Cabinet"
            sx={{ width: { xs: 96, sm: 132 }, height: 'auto', flexShrink: 0 }} />
          {account && <ButtonBase
            aria-label={`${account.name || account.email} account menu`}
            aria-haspopup="menu" aria-expanded={accountMenuOpen}
            aria-controls={accountMenuOpen ? 'plan-account-menu' : undefined}
            onClick={event => setAccountMenuAnchor(event.currentTarget)} disabled={isSubmitting}
            sx={{ display: 'flex', gap: 1, minWidth: 0,
              maxWidth: { xs: 'calc(100% - 104px)', sm: 280 }, minHeight: 44, paddingX: 1, paddingY: 0.5,
              borderRadius: 1, textAlign: 'left',
              '&.Mui-focusVisible': { outline: '2px solid', outlineColor: colors.lavendar950, outlineOffset: 2 },
            }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 11, lineHeight: '16px', color: colors.calmGrey600 }}>Signed in as</Typography>
              <Typography sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 600 }} noWrap>
                {account.name || account.email}
              </Typography>
              {account.name && <Typography sx={{ fontSize: 12, lineHeight: '18px',
                color: colors.calmGrey600 }} noWrap>
                {account.email}
              </Typography>}
            </Box>
            <CabIcon Icon={IoChevronDown} size={16} />
          </ButtonBase>}
        </Box>
        <Menu id="plan-account-menu" anchorEl={accountMenuAnchor} open={accountMenuOpen}
          onClose={() => setAccountMenuAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ list: { 'aria-label': 'Account menu' }, paper: { sx: {
            minWidth: 220, maxWidth: 'calc(100vw - 32px)', border: `1px solid ${colors.calmGrey200}`, borderRadius: 1.5,
          } } }}>
          <MenuItem disabled role="presentation" sx={{ display: 'block', paddingX: 2, paddingY: 1, borderBottom: 1,
            borderColor: colors.calmGrey200, '&.Mui-disabled': { opacity: 1 } }}>
            <Typography sx={{ fontSize: 12 }}>Signed in as</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{account?.name}</Typography>
            <Typography sx={{ fontSize: 12, overflowWrap: 'anywhere' }}>{account?.email}</Typography>
          </MenuItem>
          <MenuItem disabled={isSubmitting} onClick={() => {
            setAccountMenuAnchor(null);
            onLogout();
          }} sx={{ gap: 1.25, minHeight: 44, paddingX: 2 }}>
            <CabIcon Icon={IoLogOutOutline} size={18} />
            <Box>
              <Typography sx={{ fontSize: 14 }}>Log Out</Typography>
              <Typography sx={{ fontSize: 12 }}>This device only</Typography>
            </Box>
          </MenuItem>
        </Menu>
      </Box>
      <Box component="main" sx={{ flex: 1, width: '100%', maxWidth: 1120, marginX: 'auto',
        paddingX: { xs: 2, sm: 3, md: 4 }, paddingTop: { xs: 3, sm: 4 }, boxSizing: 'border-box',
        paddingBottom: { xs: 'calc(144px + env(safe-area-inset-bottom))', sm: 5 } }}>
        <Box sx={{ marginBottom: 3 }}>
          {onBack && <CabButton buttonType="text" size="small" onClick={onBack} disabled={isSubmitting}
            icon={<CabIcon Icon={IoArrowBack} />} sx={{ paddingX: 0, minWidth: 0, minHeight: 44, marginBottom: 1 }}>
            Back
          </CabButton>}
          <Typography variant="h1" sx={{ fontSize: { xs: 28, sm: 36 }, lineHeight: 1.2 }}>
            Choose your plan
          </Typography>
          <Typography sx={{ marginTop: 1, marginBottom: 2, fontSize: { xs: 14, sm: 16 }, color: colors.calmGrey800 }}>
            Compare what’s included, then choose the plan that fits your work.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            <Controller name="billedAnnually" control={control}
              render={({ field }) => (
                <RadioGroup row aria-label="Billing period" name={field.name}
                  value={field.value ? 'annual' : 'monthly'}
                  onChange={(_, value) => field.onChange(value === 'annual')}
                  onBlur={field.onBlur} sx={{
                    display: 'inline-flex', padding: 0.5, border: 1, borderColor: colors.calmGrey200,
                    borderRadius: 1.5, backgroundColor: colors.systemWhite,
                  }}
                >
                  {['monthly', 'annual'].map(period => (
                    <FormControlLabel key={period} value={period}
                      label={period === 'monthly' ? 'Monthly' : 'Annual'}
                      control={<Radio disabled={isSubmitting} sx={{ position: 'absolute', inset: 0, opacity: 0 }}
                        slotProps={{ input: { ref: period === 'monthly' ? field.ref : undefined } }} />}
                      sx={{
                        position: 'relative', margin: 0, paddingX: 2, minWidth: 100, minHeight: 44,
                        justifyContent: 'center', borderRadius: 1, boxSizing: 'border-box',
                        backgroundColor: field.value === (period === 'annual') ? colors.lavendar100 : 'transparent',
                        color: field.value === (period === 'annual') ? colors.lavendar950 : colors.calmGrey800,
                        border: 1,
                        borderColor: field.value === (period === 'annual') ? colors.lavendar700 : 'transparent',
                        '& .MuiTypography-root': { color: 'inherit' },
                        '& .MuiFormControlLabel-label': { fontSize: 14, fontWeight: 600 },
                        '&:has(input:focus-visible)': { outline: '2px solid', outlineOffset: 2,
                          outlineColor: colors.lavendar950 },
                      }}
                    />
                  ))}
                </RadioGroup>
              )}
            />
            {sharedAnnualSavings > 0 && <Typography sx={{ fontSize: 13, color: colors.lavendar950 }}>
              Save {formatPrice(sharedAnnualSavings)}/seat/year with annual billing
            </Typography>}
          </Box>
        </Box>

        <Table aria-label="Plan comparison" sx={{
          tableLayout: 'fixed',
          '& .MuiTableCell-root': { borderBottomColor: colors.calmGrey200, padding: { xs: 1, sm: 2 } },
        }}>
          <TableHead>
            <TableRow>
              <TableCell scope="col" sx={{ width: '38%', verticalAlign: 'bottom' }}>
                <Typography sx={{ fontSize: { xs: 13, sm: 15 }, fontWeight: 600 }}>Per seat / month</Typography>
                <Typography sx={{ fontSize: 12, marginTop: 0.5, color: colors.calmGrey600 }}>
                  {billedAnnually ? 'Billed annually' : 'Billed monthly'}
                </Typography>
              </TableCell>
              {planOptions.map(plan => {
                const cost = billedAnnually ? plan.costAnnually : plan.costMonthly;
                return (
                  <TableCell key={plan.id} scope="col" sx={{
                    textAlign: 'center', verticalAlign: 'top', borderLeft: 1, borderLeftColor: colors.calmGrey200,
                  }}>
                    <Typography variant="h2" sx={{ fontSize: { xs: 14, sm: 22 }, fontWeight: 700 }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="h2" component="p" sx={{ marginTop: 0.5,
                      fontSize: { xs: 24, sm: 30 }, lineHeight: 1.2, fontWeight: 700 }}>
                      {cost ? formatPrice(cost) : 'Free'}
                    </Typography>
                    {billedAnnually && !!cost && <Typography sx={{ fontSize: { xs: 11, sm: 13 },
                      marginTop: 0.5, color: colors.calmGrey600 }}>
                      {formatPrice(plan.costAnnually * 12)}/year
                    </Typography>}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {comparisonRows.map(row => (
              <TableRow key={row.label}>
                <TableCell component="th" scope="row">
                  <Typography sx={{ fontSize: { xs: 13, sm: 15 }, fontWeight: 600 }}>{row.label}</Typography>
                  {row.description && <Typography sx={{ marginTop: 0.5,
                    fontSize: { xs: 11, sm: 13 }, color: colors.calmGrey600 }}>
                    {row.description}
                  </Typography>}
                </TableCell>
                {planOptions.map(plan => {
                  const value = row.values[plan.id];
                  return <TableCell key={plan.id} sx={{
                    textAlign: 'center', fontSize: { xs: 13, sm: 15 },
                    borderLeft: 1, borderLeftColor: colors.calmGrey200,
                  }}>
                    {value === true ? <Box component="span" role="img" aria-label="Included"
                      sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                      <CabIcon Icon={IoCheckmark} size={22} color={colors.lavendar950} />
                    </Box> : value === false ?
                      <Box component="span" role="img" aria-label="Not included">—</Box> : value ?? 'See details'}
                  </TableCell>;
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{
          position: { xs: 'fixed', sm: 'static' }, bottom: 0, left: 0, right: 0,
          zIndex: theme => theme.zIndex.appBar,
          display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '38% 31% 31%' },
          padding: { xs: 1.5, sm: 0 }, paddingBottom: { xs: 'calc(12px + env(safe-area-inset-bottom))', sm: 0 },
          boxShadow: { xs: 3, sm: 'none' }, gap: { xs: 1, sm: 0 },
          borderTop: { xs: `1px solid ${colors.calmGrey200}`, sm: 0 },
          backgroundColor: colors.calmGrey50,
        }}>
          <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, padding: { xs: 0, sm: 2 },
            textAlign: { xs: 'center', sm: 'left' }, alignSelf: 'center' }}>
            <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 15, fontWeight: 600 }}>
              Choose your plan
            </Typography>
            <Typography sx={{ fontSize: 12 }}>{billedAnnually ? 'Annual billing' : 'Monthly billing'}</Typography>
          </Box>
          {planOptions.map(plan => <Box key={plan.id} sx={{ padding: { xs: 0, sm: 2 }, textAlign: 'center' }}>
            <Typography sx={{ display: { xs: 'block', sm: 'none' }, fontSize: 14, fontWeight: 600, marginBottom: 0.5 }}>
              {plan.name}
            </Typography>
            <CabButton onClick={() => choosePlan(plan.id)} disabled={isSubmitting}
              sx={{ width: '100%', maxWidth: 240, minHeight: 44, fontSize: { xs: 12, sm: 14 }, paddingX: 1 }}>
              {plan.id === NEW_TIER.CORE ? "Start Free Trial" :
                plan.id === NEW_TIER.STRATEGIC ? "Choose Plan" : "Continue"}
            </CabButton>
          </Box>)}
        </Box>

      </Box>
      <CabModal isAlert open={!!dialogError} text={dialogError}
        aria-labelledby="Payment start failed" aria-describedby="Payment start failed"
        actionButtons={<CabButton onClick={onDismissError}>Close</CabButton>}
      />
    </Box>
  );
};

export default PlanSelection;
